import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { useI18n } from "../i18n/I18nContext";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import {
  ArrowRight, Radio, Shield, Vibrate, Lightbulb, ChevronRight,
  Truck, Mountain, HardHat, Flame, Activity, Wifi, Zap, Cpu,
  CheckCircle2, AlertTriangle, FileText, Download,
} from "lucide-react";

const IMG_HERO     = "https://customer-assets.emergentagent.com/job_atlas-shield-hub/artifacts/g3re2fzc_Safe%20Link.jpeg";
const IMG_INDUSTRY = "https://customer-assets.emergentagent.com/job_atlas-shield-hub/artifacts/b8bqeq8l_SafeLink__.jpeg";
const IMG_ZONES    = "https://customer-assets.emergentagent.com/job_atlas-shield-hub/artifacts/zg8y66dj_SafeLink%20Overview.jpeg";
const IMG_HM       = "https://customer-assets.emergentagent.com/job_atlas-shield-hub/artifacts/z6bhjisz_SafeLink_-.jpeg";
const PDF_MANUAL   = "https://customer-assets.emergentagent.com/job_atlas-shield-hub/artifacts/x447radd_Safelink%20UA350%20Manual%20del%20usuario.pdf";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || "";

const HARDWARE = [
  { id: "base",     icon: Radio,     titleKey: "hwBaseTitle",     descKey: "hwBaseDesc",     specsKey: "hwBaseSpecs" },
  { id: "bracelet", icon: Vibrate,   titleKey: "hwBraceletTitle", descKey: "hwBraceletDesc", specsKey: "hwBraceletSpecs" },
  { id: "alarm",    icon: Lightbulb, titleKey: "hwAlarmTitle",    descKey: "hwAlarmDesc",    specsKey: "hwAlarmSpecs" },
];

const ZONES = [
  { id: "z1", labelKey: "zone1Label", titleKey: "zone1Title", descKey: "zone1Desc", rangeKey: "zone1Range", color: "#FF1F4D" },
  { id: "z2", labelKey: "zone2Label", titleKey: "zone2Title", descKey: "zone2Desc", rangeKey: "zone2Range", color: "#FFB800" },
  { id: "z3", labelKey: "zone3Label", titleKey: "zone3Title", descKey: "zone3Desc", rangeKey: "zone3Range", color: "#0E9F6E" },
];

const SPECS = [
  { labelKey: "specTech",       valKey: "specTechVal",       icon: Wifi },
  { labelKey: "specRange",      valKey: "specRangeVal",      icon: Activity },
  { labelKey: "specPrecision",  valKey: "specPrecisionVal",  icon: Cpu },
  { labelKey: "specProtection", valKey: "specProtectionVal", icon: Shield },
  { labelKey: "specComm",       valKey: "specCommVal",       icon: Radio },
  { labelKey: "specPower",      valKey: "specPowerVal",      icon: Zap },
];

const INDUSTRIES = [
  { id: "logistics",    icon: Truck,    titleKey: "indLogistics",    descKey: "indLogisticsDesc" },
  { id: "mining",       icon: Mountain, titleKey: "indMining",       descKey: "indMiningDesc" },
  { id: "construction", icon: HardHat,  titleKey: "indConstruction", descKey: "indConstructionDesc" },
  { id: "oilgas",       icon: Flame,    titleKey: "indOilGas",       descKey: "indOilGasDesc" },
];

const SafeLink = () => {
  const { t } = useI18n();

  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "", country: "", requirements: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [captchaEnabled, setCaptchaEnabled] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/inquiries/captcha-config`)
      .then((r) => setCaptchaEnabled(Boolean(r?.data?.enabled)))
      .catch(() => setCaptchaEnabled(false));
  }, []);

  const handleChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (captchaEnabled && !captchaToken) {
      setFeedback({ ok: false, msg: t("sygnal.formCaptchaRequired") });
      return;
    }
    setSubmitting(true);
    setFeedback(null);
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        type: "safelink",
        captcha_token: captchaToken || undefined,
      });
      setFeedback({ ok: true, msg: t("sygnal.formSuccess") });
      setForm({ name: "", email: "", company: "", phone: "", country: "", requirements: "" });
      if (captchaRef.current) captchaRef.current.resetCaptcha();
      setCaptchaToken(null);
    } catch (err) {
      const status = err?.response?.status;
      let msg = t("sygnal.formError");
      if (status === 429) msg = t("sygnal.formRateLimit");
      else if (status === 403) msg = t("sygnal.formCaptchaFailed");
      setFeedback({ ok: false, msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="safelink-page">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(110deg, rgba(5,5,5,0.96) 0%, rgba(5,5,5,0.85) 40%, rgba(5,5,5,0.55) 75%, rgba(5,5,5,0.7) 100%), url(${IMG_HERO})`,
            backgroundSize: "cover",
            backgroundPosition: "center right",
          }}
        />
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-12 gap-6 items-center">
            <div className="col-span-12 lg:col-span-8">
              <p className="tactical-label" data-testid="safelink-eyebrow">
                <span className="inline-block w-8 h-px bg-[#19A7E0] mr-3 align-middle" />
                {t("safelink.eyebrow")}
              </p>
              <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[0.95]" data-testid="safelink-title">
                {t("safelink.heroTitle")}
              </h1>
              <p className="mt-7 text-base lg:text-lg text-white/70 max-w-2xl leading-relaxed">
                {t("safelink.heroSubtitle")}
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <a
                  href="#coverage-form"
                  data-testid="safelink-cta-primary"
                  className="inline-flex items-center gap-2 px-6 py-3.5 btn-primary text-sm font-mono uppercase tracking-[0.18em] rounded-sm"
                >
                  {t("safelink.ctaPrimary")} <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href={PDF_MANUAL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="safelink-cta-manual-hero"
                  className="inline-flex items-center gap-2 px-6 py-3.5 btn-ghost text-sm font-mono uppercase tracking-[0.18em] rounded-sm"
                >
                  <Download className="w-4 h-4" /> {t("safelink.ctaSecondary")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HARDWARE OVERVIEW */}
      <section className="relative py-24 lg:py-32 border-t border-white/[0.08]" data-testid="safelink-hardware">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="tactical-label">{t("safelink.overviewEyebrow")}</p>
            <h2 className="mt-4 text-3xl lg:text-5xl font-medium tracking-tight">
              {t("safelink.overviewTitle")}
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left: real industrial photo */}
            <div
              className="lg:col-span-5 card-sharp min-h-[360px] relative overflow-hidden"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0.35) 0%, rgba(5,5,5,0.92) 100%), url(${IMG_INDUSTRY})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              data-testid="safelink-industry-photo"
            >
              <div className="absolute top-6 left-6 tactical-label">UA350 · IN-FIELD</div>
              <div className="absolute bottom-6 left-6 right-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#19A7E0]">
                  Live deployment · multi-vehicle / multi-tag
                </p>
              </div>
            </div>

            {/* Right: hardware cards */}
            <div className="lg:col-span-7 grid grid-cols-1 gap-px bg-white/[0.08] border border-white/[0.08]">
              {HARDWARE.map((h, i) => {
                const Icon = h.icon;
                return (
                  <article
                    key={h.id}
                    data-testid={`safelink-hw-${h.id}`}
                    className="bg-[#050505] p-6 lg:p-8 hover:bg-[#0A0D14] transition-colors flex items-start gap-5"
                  >
                    <div className="w-12 h-12 flex-shrink-0 bg-[#19A7E0]/10 border border-[#19A7E0]/30 rounded-sm flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#19A7E0]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/40">
                        {String(i + 1).padStart(2, "0")} / 03
                      </p>
                      <h3 className="mt-2 text-lg font-medium tracking-tight">
                        {t(`safelink.${h.titleKey}`)}
                      </h3>
                      <p className="mt-2 text-sm text-white/65 leading-relaxed">
                        {t(`safelink.${h.descKey}`)}
                      </p>
                      <p className="mt-3 inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-[#19A7E0]">
                        ◉ {t(`safelink.${h.specsKey}`)}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* SAFETY ZONES */}
      <section className="relative py-24 border-t border-white/[0.08]" data-testid="safelink-zones">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="tactical-label">{t("safelink.zonesEyebrow")}</p>
            <h2 className="mt-4 text-3xl lg:text-5xl font-medium tracking-tight">
              {t("safelink.zonesTitle")}
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div
              className="lg:col-span-7 card-sharp min-h-[400px] relative overflow-hidden bg-black"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.65) 100%), url(${IMG_ZONES})`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
              }}
              data-testid="safelink-zones-diagram"
            >
              <div className="absolute top-6 left-6 tactical-label">PERIMETER MAP · CM-PRECISION</div>
            </div>

            <div className="lg:col-span-5 grid grid-cols-1 gap-3">
              {ZONES.map((z) => (
                <article
                  key={z.id}
                  data-testid={`safelink-zone-${z.id}`}
                  className="card-sharp p-5 flex gap-4 items-start"
                >
                  <span
                    className="mt-1 w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: z.color, boxShadow: `0 0 12px ${z.color}` }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: z.color }}>
                      {t(`safelink.${z.labelKey}`)} · {t(`safelink.${z.rangeKey}`)}
                    </p>
                    <h3 className="mt-1 text-base font-medium tracking-tight">
                      {t(`safelink.${z.titleKey}`)}
                    </h3>
                    <p className="mt-1.5 text-sm text-white/65 leading-relaxed">
                      {t(`safelink.${z.descKey}`)}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SPECS */}
      <section className="relative py-24 border-t border-white/[0.08]" data-testid="safelink-specs">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="tactical-label">{t("safelink.specsEyebrow")}</p>
            <h2 className="mt-4 text-3xl lg:text-4xl font-medium tracking-tight">
              {t("safelink.specsTitle")}
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.08] border border-white/[0.08]">
            {SPECS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="bg-[#050505] p-6 lg:p-7" data-testid={`safelink-spec-${i}`}>
                  <Icon className="w-5 h-5 text-[#19A7E0]" />
                  <p className="mt-4 tactical-label">{t(`safelink.${s.labelKey}`)}</p>
                  <p className="mt-2 text-lg lg:text-xl font-medium tracking-tight">
                    {t(`safelink.${s.valKey}`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="relative py-24 border-t border-white/[0.08]" data-testid="safelink-industries">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="tactical-label">{t("safelink.industriesEyebrow")}</p>
            <h2 className="mt-4 text-3xl lg:text-4xl font-medium tracking-tight">
              {t("safelink.industriesTitle")}
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.08] border border-white/[0.08]">
            {INDUSTRIES.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <article
                  key={ind.id}
                  data-testid={`safelink-industry-${ind.id}`}
                  className="bg-[#050505] p-7 hover:bg-[#0A0D14] transition-colors min-h-[260px] flex flex-col"
                >
                  <Icon className="w-7 h-7 text-[#19A7E0]" />
                  <p className="mt-5 text-[10px] font-mono uppercase tracking-[0.25em] text-white/40">
                    {String(i + 1).padStart(2, "0")} / 04
                  </p>
                  <h3 className="mt-2 text-lg font-medium tracking-tight">
                    {t(`safelink.${ind.titleKey}`)}
                  </h3>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed flex-1">
                    {t(`safelink.${ind.descKey}`)}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* DOWNLOADS */}
      <section className="relative py-24 border-t border-white/[0.08]" data-testid="safelink-downloads">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="tactical-label">{t("safelink.downloadsEyebrow")}</p>
            <h2 className="mt-4 text-3xl lg:text-4xl font-medium tracking-tight">
              {t("safelink.downloadsTitle")}
            </h2>
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <a
              href={PDF_MANUAL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="safelink-download-manual"
              className="lg:col-span-7 card-sharp p-6 lg:p-8 flex items-start gap-5 hover:bg-[#0A0D14] transition-colors"
            >
              <div className="w-14 h-14 flex-shrink-0 bg-[#19A7E0]/10 border border-[#19A7E0]/30 rounded-sm flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#19A7E0]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="tactical-label text-[10px]">PDF · ES · UA350</p>
                <h3 className="mt-2 text-lg font-medium tracking-tight">
                  {t("safelink.downloadManual")}
                </h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">
                  {t("safelink.downloadManualDesc")}
                </p>
              </div>
              <span className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-[#19A7E0] shrink-0">
                {t("safelink.downloadOpen")} <ChevronRight className="w-4 h-4" />
              </span>
            </a>

            <div
              className="lg:col-span-5 card-sharp min-h-[200px] relative overflow-hidden"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0.35) 0%, rgba(5,5,5,0.95) 100%), url(${IMG_HM})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              data-testid="safelink-hm-card"
            >
              <div className="absolute inset-0 p-6 lg:p-7 flex flex-col justify-end">
                <p className="tactical-label text-[10px] text-[#19A7E0]">SafeLink-HM · Variant</p>
                <p className="mt-2 text-base font-medium tracking-tight">
                  Vehicle Interlock Module
                </p>
                <p className="mt-2 text-xs text-white/65 leading-relaxed">
                  Hardware override that throttles or stops the vehicle on emergency-zone breach.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COVERAGE STUDY FORM */}
      <section
        id="coverage-form"
        className="relative py-24 lg:py-32 border-t border-white/[0.08]"
        data-testid="safelink-form-section"
      >
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="tactical-label">{t("safelink.eyebrow")}</p>
              <h2 className="mt-4 text-3xl lg:text-4xl font-medium tracking-tight">
                {t("safelink.formTitle")}
              </h2>
              <p className="mt-5 text-base text-white/65 leading-relaxed">
                {t("safelink.formDesc")}
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="lg:col-span-7 card-sharp p-6 lg:p-8 bg-[#0A0D14]"
              data-testid="safelink-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t("sygnal.formName")} testid="form-name" required
                       value={form.name} onChange={handleChange("name")} />
                <Field label={t("sygnal.formEmail")} testid="form-email" required type="email"
                       value={form.email} onChange={handleChange("email")} />
                <Field label={t("sygnal.formCompany")} testid="form-company"
                       value={form.company} onChange={handleChange("company")} />
                <Field label={t("sygnal.formPhone")} testid="form-phone"
                       value={form.phone} onChange={handleChange("phone")} />
                <Field label={t("sygnal.formCountry")} testid="form-country"
                       value={form.country} onChange={handleChange("country")} />
              </div>

              <div className="mt-4">
                <label className="block tactical-label mb-2">{t("safelink.formRequirements")}</label>
                <textarea
                  required
                  data-testid="form-requirements"
                  value={form.requirements}
                  onChange={handleChange("requirements")}
                  placeholder={t("safelink.formRequirementsPh")}
                  rows={5}
                  className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-sm text-white placeholder-white/30 focus:border-[#19A7E0] focus:outline-none rounded-sm"
                />
              </div>

              {captchaEnabled && HCAPTCHA_SITE_KEY && (
                <div className="mt-5" data-testid="form-captcha">
                  <HCaptcha
                    ref={captchaRef}
                    sitekey={HCAPTCHA_SITE_KEY}
                    theme="dark"
                    onVerify={(tk) => setCaptchaToken(tk)}
                    onExpire={() => setCaptchaToken(null)}
                    onError={() => setCaptchaToken(null)}
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="form-submit"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 btn-primary text-sm font-mono uppercase tracking-[0.18em] rounded-sm disabled:opacity-60"
              >
                {submitting ? t("sygnal.formSending") : t("safelink.formSubmit")}
                {!submitting && <ArrowRight className="w-4 h-4" />}
              </button>

              {feedback && (
                <div
                  data-testid={feedback.ok ? "form-success" : "form-error"}
                  className={`mt-5 flex items-start gap-2 text-sm ${
                    feedback.ok ? "text-[#0E9F6E]" : "text-[#FF6A3D]"
                  }`}
                >
                  {feedback.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertTriangle className="w-4 h-4 mt-0.5" />}
                  <span>{feedback.msg}</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const Field = ({ label, testid, value, onChange, type = "text", required = false }) => (
  <div>
    <label className="block tactical-label mb-2">
      {label}{required && <span className="text-[#19A7E0]"> *</span>}
    </label>
    <input
      type={type}
      required={required}
      data-testid={testid}
      value={value}
      onChange={onChange}
      className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-sm text-white placeholder-white/30 focus:border-[#19A7E0] focus:outline-none rounded-sm"
    />
  </div>
);

export default SafeLink;
